import React, { createContext, useState, useCallback } from 'react';
import { Snackbar, Alert, Button, Box } from '@mui/material';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info', // 'success', 'error', 'info', 'warning'
    duration: 5000,
    actions: [] // Array of action buttons
  });

  const showNotification = useCallback((messageOrData, type) => {
    // Handle both calling patterns:
    // 1. showNotification({ message: '...', type: '...' })
    // 2. showNotification('message', 'type')
    let notificationData;
    if (typeof messageOrData === 'string') {
      notificationData = {
        message: messageOrData,
        type: type || 'info'
      };
    } else {
      notificationData = messageOrData;
    }

    // Close any existing notification first
    setNotification(prev => ({
      ...prev,
      open: false
    }));

    // For immediate notifications (like success messages), show immediately
    // For others, use timeout to prevent animation issues
    const shouldShowImmediately = notificationData.immediate === true ||
      notificationData.type === 'success' ||
      (notificationData.message && notificationData.message.toLowerCase().includes('success'));

    const showNewNotification = () => {
      setNotification({
        open: true,
        message: notificationData.message,
        type: notificationData.type || 'info',
        duration: notificationData.autoHideDuration || notificationData.duration || 5000,
        actions: notificationData.actions || []
      });

      // Log notification for debugging
      console.log(`Showing notification: ${notificationData.message} (${notificationData.type || 'info'})`);
    };
    
    if (shouldShowImmediately) {
      // Show immediately for success messages
      showNewNotification();
    } else {
      // Use setTimeout to ensure the previous notification is closed 
      // before showing the new one (prevents animation issues)
      setTimeout(showNewNotification, 100);
    }
  }, []);

  const hideNotification = useCallback(() => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  }, []);

  const value = {
    showNotification,
    hideNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={hideNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          zIndex: 9999 // Ensure it's above everything else
        }}
      >
        <Alert 
          onClose={hideNotification} 
          severity={notification.type}
          variant="filled"
          elevation={6}
          sx={{ 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1rem',
            padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
          action={
            notification.actions && notification.actions.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                {notification.actions.map((action, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    size="small"
                    onClick={() => {
                      hideNotification();
                      if (action.onClick) action.onClick();
                    }}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            ) : null
          }
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};